/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import * as React from "react";
import {
    DetailsListLayoutMode,
    IColumn,
    SelectionMode,
    ICommandBarItemProps,
    DialogType,
    DefaultButton,
    Dialog,
    DialogFooter,
    PrimaryButton
} from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import Grid from "../../Grid/Grid";
import {
    EnvironmentSettingCommands,
    IEnvironmentViewProps,
    IEnvironmentViewState
} from "./EnvironmentViewModels";
import { EnvironmentPanel } from "../Panels/EnvironmentPanel/EnvironmentPanel";
import { IGridItem } from "../../Grid/GridModels";

const envSettingCommands = new EnvironmentSettingCommands();
const cmds = envSettingCommands.Cmds;

const EnvironmentSettingView = (props: IEnvironmentViewProps): JSX.Element => {
    const tableColumns: IColumn[] = [
        {
            key: "nameCol",
            name: "Setting name",
            fieldName: "id",
            minWidth: 100,
            maxWidth: 200,
            isResizable: true
        },
        {
            key: "environment",
            name: "Environment",
            fieldName: "Environment",
            minWidth: 100,
            maxWidth: 200,
            isResizable: true
        },
        {
            key: "value",
            name: "Value",
            fieldName: "DefaultValue",
            minWidth: 100,
            maxWidth: 200,
            isResizable: true
        }
    ];

    const initialState: IEnvironmentViewState = {
        currentIndex: -1,
        selectedRows: [],
        definition: props.definition
    };

    const [state, dispatch] = React.useReducer(envSettingCommands.Reducer, initialState);

    React.useEffect(() => {
        props.advancedViewVM.dispatch(props.advancedViewVM.cmds.definitionChange(state.definition));
    }, [state.definition]);

    const [hideDialog, { toggle: toggleHideDialog }] = useBoolean(true);

    const dialogContentProps = {
        type: DialogType.normal,
        title: "Delete environment default?",
        subText: `Are you sure you want to delete ${state.selectedRows.length} environment default(s)?`
    };

    const onDeleteConfirm = () => {
        dispatch(cmds.onDelete());
        toggleHideDialog();
    };

    const _defaultButtonDefinitions: ICommandBarItemProps[] = [
        {
            key: "add",
            text: "Add default",
            iconProps: { iconName: "Add" },
            onClick: () => dispatch(cmds.onAdd())
        }
    ];

    const _oneSelectionButtonDefinitions: ICommandBarItemProps[] = [
        ..._defaultButtonDefinitions,
        {
            key: "edit",
            text: "Edit",
            iconProps: { iconName: "Edit" },
            onClick: () => dispatch(cmds.onEdit())
        },
        {
            key: "delete",
            text: "Delete",
            iconProps: { iconName: "Delete" },
            onClick: toggleHideDialog
        }
    ];

    const _multipleSelectionButtonDefinitions: ICommandBarItemProps[] = [
        ..._defaultButtonDefinitions,
        {
            key: "delete",
            text: "Delete",
            iconProps: { iconName: "Delete" },
            onClick: toggleHideDialog
        }
    ];

    const items = React.useMemo(
        () =>
            state.definition.EnvironmentalDefaultsOverride.slice(0)
                .map((setting, index) => {
                    const { SettingName, ...rest } = setting;
                    return { id: SettingName, index, ...rest };
                })
                .sort((a: IGridItem, b: IGridItem) => (a.id < b.id ? -1 : 1)),
        [state.definition.EnvironmentalDefaultsOverride]
    );

    const actions = {
        left:
            state.selectedRows.length === 1
                ? _oneSelectionButtonDefinitions
                : state.selectedRows.length > 1
                  ? _multipleSelectionButtonDefinitions
                  : _defaultButtonDefinitions
    };

    return (
        <>
            <Grid
                id="environmentSettings"
                columns={tableColumns}
                items={items ?? []}
                layout={DetailsListLayoutMode.justified}
                selectionMode={SelectionMode.multiple}
                actions={actions}
                hasFilter
                filterColumns={["id", "Environment", "DefaultValue"]}
                onSelectRow={selectedRows => dispatch(cmds.onSelectionChanged(selectedRows))}
            />

            {state.currentPanelProps?.data && (
                <EnvironmentPanel
                    onClosePanel={() => dispatch(cmds.onCancel())}
                    onSaveContent={data => dispatch(cmds.onSave(data))}
                    {...state.currentPanelProps}
                />
            )}

            <Dialog
                hidden={hideDialog}
                onDismiss={toggleHideDialog}
                dialogContentProps={dialogContentProps}
                modalProps={{ isBlocking: true }}
                maxWidth={560}
                minWidth={560}
            >
                <DialogFooter>
                    <PrimaryButton onClick={onDeleteConfirm} text="Yes, delete" />
                    <DefaultButton onClick={toggleHideDialog} text="Cancel" />
                </DialogFooter>
            </Dialog>
        </>
    );
};

export default EnvironmentSettingView;
